import React, { useState, useEffect, useRef } from 'react';
import { SystemLog } from '../types';

interface TerminalActivityLogsProps {
  logs: SystemLog[];
  onClear?: () => void;
  maxHeight?: string;
  title?: string;
}

const TerminalActivityLogs: React.FC<TerminalActivityLogsProps> = ({ 
  logs, 
  onClear, 
  maxHeight = '400px',
  title = 'Terminal Activity'
}) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [filterLevel, setFilterLevel] = useState<'ALL' | SystemLog['level']>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const logsEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      (() => { const el = logsEndRef.current; if(el && el.parentElement) { el.parentElement.scrollTop = el.parentElement.scrollHeight; } })()
    }
  }, [logs, autoScroll]);

  // Filter logs based on selected level and search term
  const filteredLogs = logs
    .filter(log => filterLevel === 'ALL' || log.level === filterLevel)
    .filter(log => 
      searchTerm === '' || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.component && log.component.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR': return 'text-red-400 border-red-500/30 bg-red-900/20';
      case 'WARN': return 'text-yellow-400 border-yellow-500/30 bg-yellow-900/20';
      case 'INFO': return 'text-blue-400 border-blue-500/30 bg-blue-900/20';
      case 'DEBUG': return 'text-purple-400 border-purple-500/30 bg-purple-900/20';
      case 'AI_ACTION': return 'text-green-400 border-green-500/30 bg-green-900/20';
      default: return 'text-slate-400 border-slate-500/30 bg-slate-900/20';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'ERROR': return 'fa-exclamation-circle';
      case 'WARN': return 'fa-exclamation-triangle';
      case 'INFO': return 'fa-info-circle';
      case 'DEBUG': return 'fa-bug';
      case 'AI_ACTION': return 'fa-robot';
      default: return 'fa-terminal';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Convert to more readable format if needed
    return timestamp;
  };

  return (
    <div className="bg-[#1e222d] rounded-xl border border-[#2a2e39] overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="p-4 border-b border-[#2a2e39] bg-[#131722]/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <i className="fas fa-wave-square text-purple-500 animate-pulse"></i>
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {title}
            </h3>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-[8px] font-bold text-green-500 uppercase">LIVE</span>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#131722] border border-[#2a2e39] rounded text-[9px] font-bold text-slate-400 px-2 py-1 uppercase tracking-wider focus:outline-none focus:border-purple-500 w-32"
            />
            <i className="fas fa-search text-[8px] text-slate-500 absolute right-2 top-1/2 transform -translate-y-1/2"></i>
          </div>
          
          {/* Level Filter */}
          <select 
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value as any)}
            className="bg-[#131722] border border-[#2a2e39] rounded text-[9px] font-bold text-slate-400 px-2 py-1 uppercase tracking-wider focus:outline-none focus:border-purple-500"
          >
            <option value="ALL">ALL</option>
            <option value="ERROR">ERROR</option>
            <option value="WARN">WARN</option>
            <option value="INFO">INFO</option>
            <option value="DEBUG">DEBUG</option>
            <option value="AI_ACTION">AI</option>
          </select>
          
          {/* Auto-scroll toggle */}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`px-2 py-1 text-[9px] font-black uppercase rounded transition-all ${
              autoScroll 
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/30' 
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
          >
            {autoScroll ? 'AUTO' : 'MANUAL'}
          </button>
          
          {/* Clear button */}
          {onClear && (
            <button
              onClick={onClear}
              className="px-2 py-1 bg-red-600/20 border border-red-500/30 text-red-400 text-[9px] font-black uppercase rounded hover:bg-red-600 hover:text-white transition-all"
            >
              <i className="fas fa-trash mr-1"></i> CLEAR
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="px-4 py-2 border-b border-[#2a2e39] bg-[#131722]/40 flex justify-between text-[9px] font-mono">
        <div className="flex gap-4">
          <span className="text-slate-500">
            Total: <span className="text-white font-bold">{logs.length}</span>
          </span>
          <span className="text-slate-500">
            Filtered: <span className="text-white font-bold">{filteredLogs.length}</span>
          </span>
          {searchTerm && (
            <span className="text-slate-500">
              Search: <span className="text-yellow-400 font-bold">'{searchTerm}'</span>
            </span>
          )}
        </div>
        <div className="text-slate-500">
          Last Update: <span className="text-white font-bold">
            {logs.length > 0 ? formatTimestamp(logs[0].timestamp) : 'Never'}
          </span>
        </div>
      </div>

      {/* Logs Container */}
      <div 
        ref={containerRef}
        className="p-3 font-mono text-[10px] overflow-y-auto custom-scrollbar"
        style={{ maxHeight }}
      >
        {filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <i className="fas fa-terminal text-2xl mb-2 block"></i>
            <p className="text-[11px] font-bold uppercase tracking-widest">
              {searchTerm ? 'No Matching Logs' : 'No Logs Available'}
            </p>
            <p className="text-[9px] mt-1">
              {searchTerm ? `No logs match "${searchTerm}"` : 'Waiting for system activity...'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLogs.map((log, index) => (
              <div 
                key={`${log.id}-${index}`}
                className={`border-l-2 pl-3 py-2 rounded-r ${getLevelColor(log.level)} border-opacity-50 transition-all hover:opacity-90`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <i className={`fas ${getLevelIcon(log.level)} text-xs`}></i>
                    <span className="text-[8px] font-black text-slate-500 opacity-70">
                      [{formatTimestamp(log.timestamp)}]
                    </span>
                    {log.component && (
                      <span className="text-[8px] bg-slate-700/50 text-slate-400 px-1.5 py-0.5 rounded font-bold uppercase">
                        {log.component}
                      </span>
                    )}
                  </div>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${
                    log.level === 'ERROR' ? 'bg-red-500/30 text-red-300' :
                    log.level === 'WARN' ? 'bg-yellow-500/30 text-yellow-300' :
                    log.level === 'INFO' ? 'bg-blue-500/30 text-blue-300' :
                    log.level === 'DEBUG' ? 'bg-purple-500/30 text-purple-300' :
                    log.level === 'AI_ACTION' ? 'bg-green-500/30 text-green-300' :
                    'bg-slate-500/30 text-slate-300'
                  }`}>
                    {log.level}
                  </span>
                </div>
                <p className="text-slate-200 leading-relaxed break-words pl-5">
                  {log.message}
                </p>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-[#2a2e39] bg-[#131722]/40 text-[8px] text-slate-500 font-mono flex justify-between">
        <span>OMNIGATE • Terminal Activity Monitor</span>
        <span>Gateway: 8888 • {new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default TerminalActivityLogs;